/**
 * Landing Page Script
 * Handles navigation auth state, smooth scrolling, and animations
 */

import { checkStudentAuth, checkGuardianAuth } from '../src/utils/auth-helpers';
import { updateNavWithUser } from '../src/utils/nav-helpers';

// ========================================
// Authentication & Navigation
// ========================================

(async () => {
  // Check if student is logged in
  const student = checkStudentAuth();
  if (student) {
    updateNavWithUser(student);
    return;
  }

  // Check if guardian (parent/teacher) is logged in
  const guardian = await checkGuardianAuth();
  if (guardian) {
    updateNavWithUser(guardian);
  }
})();

// ========================================
// Smooth Scrolling
// ========================================

// Smooth scrolling for anchor links
document.querySelectorAll<HTMLAnchorElement>('a[href^="#"]').forEach((anchor) => {
  anchor.addEventListener('click', function (this: HTMLAnchorElement, e: Event) {
    const href = this.getAttribute('href');

    // Skip if it's just "#" or null
    if (!href || href === '#') {
      e.preventDefault();
      return;
    }

    const target = document.querySelector(href);
    if (target) {
      e.preventDefault();
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  });
});

// ========================================
// Intersection Observer Animations
// ========================================

const observerOptions = {
  threshold: 0.1,
  rootMargin: '0px 0px -50px 0px',
};

const observer = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      entry.target.classList.add('visible');
    }
  });
}, observerOptions);

// Observe all sections
document.querySelectorAll('.section').forEach((section) => {
  section.classList.add('fade-in-section');
  observer.observe(section);
});

// ========================================
// Feature Cards Animation
// ========================================

// Add animation classes to feature cards
document.querySelectorAll('.feature-card').forEach((card, index) => {
  (card as HTMLElement).style.animationDelay = `${index * 0.1}s`;
});

// ========================================
// URL Parameter Handling
// ========================================

// Handle URL parameters for account type
window.addEventListener('DOMContentLoaded', () => {
  const urlParams = new URLSearchParams(window.location.search);
  const accountType = urlParams.get('type');

  // If there's an account type in URL, scroll to relevant section
  if (accountType === 'parent') {
    document.querySelector('#for-parents')?.scrollIntoView({ behavior: 'smooth' });
  } else if (accountType === 'teacher') {
    document.querySelector('#for-teachers')?.scrollIntoView({ behavior: 'smooth' });
  }
});

// ========================================
// Topic Cards Hover Effects
// ========================================

// Add hover effect to topic cards
document.querySelectorAll<HTMLElement>('.topic-card').forEach((card) => {
  card.addEventListener('mouseenter', function (this: HTMLElement) {
    if (!this.classList.contains('active')) {
      this.style.borderColor = 'var(--brand-primary)';
    }
  });

  card.addEventListener('mouseleave', function (this: HTMLElement) {
    if (!this.classList.contains('active')) {
      this.style.borderColor = 'transparent';
    }
  });
});

// ========================================
// Developer Console Message
// ========================================

console.log('%c🚀 SciQuest Heroes', 'color: #2563eb; font-size: 24px; font-weight: bold;');
console.log('%cMaking science education fun and interactive!', 'color: #6b7280; font-size: 14px;');
